/**
 * Product Card Navigation Handler
 * Ensures smooth transition by showing the main image before navigation
 * when user clicks on the card while hovering (showing second image)
 */

if (!customElements.get('product-card')) {
  customElements.define(
    'product-card',
    class ProductCard extends HTMLElement {
      constructor() {
        super();
        this.imageLink = null;
        this.imageWrapper = null;
        this.secondImage = null;
        this.primaryImage = null;
      }

      connectedCallback() {
        this.imageLink = this.querySelector('.product-card__image-wrapper a');
        this.imageWrapper = this.querySelector('.product-card__image-wrapper');
        this.secondImage = this.querySelector('.product-card__image--second');
        this.primaryImage = this.querySelector('.product-card__image--main');

        if (!this.imageLink || !this.secondImage) return;

        // Handle click
        this.imageLink.addEventListener('click', (e) => {
          this.handleClick(e);
        });

        // Reset state when page is restored from bfcache (browser back button)
        window.addEventListener('pageshow', (event) => {
          if (event.persisted) {
            // Page was restored from bfcache - reset any disabled states
            this.resetState();
          }
        });
      }

      resetState() {
        // Remove force-unhover class
        if (this.imageLink) {
          this.imageLink.classList.remove('force-unhover');
        }

        // Re-enable pointer events
        if (this.imageWrapper) {
          this.imageWrapper.style.pointerEvents = '';
        }

        // Reset opacity and transition styles
        if (this.primaryImage) {
          this.primaryImage.style.opacity = '';
          this.primaryImage.style.transition = '';
        }

        if (this.secondImage) {
          this.secondImage.style.opacity = '';
          this.secondImage.style.transition = '';
        }
      }

      handleClick(event) {
        // Check if second image exists and if wrapper is currently being hovered
        if (!this.secondImage) {
          return; // No second image - allow normal navigation
        }

        // Check if the image wrapper is being hovered (which shows second image via CSS)
        const isHovered = this.imageWrapper.matches(':hover');

        if (!isHovered) {
          return; // Not hovering - allow normal navigation
        }

        // We're hovering and second image is visible - intercept click
        event.preventDefault();

        const targetUrl = this.imageLink.getAttribute('href');

        // Preload the target product's main image during the transition
        this.preloadTargetImage();

        // Crossfade effect: Fade out secondary, fade in primary simultaneously
        // Set initial opacity for primary image to create smooth crossfade
        if (this.primaryImage) {
          this.primaryImage.style.transition = 'opacity 0.3s ease-in-out';
          this.primaryImage.style.opacity = '0.7';

          // Fade in primary to full opacity
          requestAnimationFrame(() => {
            this.primaryImage.style.opacity = '1';
          });
        }

        // Fade out secondary image
        this.secondImage.style.transition = 'opacity 0.3s ease-in-out';
        this.secondImage.style.opacity = '0';

        // Track if navigation has already occurred
        let hasNavigated = false;

        // Function to navigate (only once)
        const navigate = () => {
          if (!hasNavigated) {
            hasNavigated = true;
            window.location.href = targetUrl;
          }
        };

        // Wait for the CSS transition to complete
        const handleTransitionEnd = (e) => {
          if (e.propertyName === 'opacity') {
            // Clear the fallback timeout
            clearTimeout(fallbackTimer);
            // Primary image is now visible - navigate to product page
            navigate();
          }
        };

        // Listen for transitionend on the second image
        this.secondImage.addEventListener('transitionend', handleTransitionEnd, { once: true });

        // Fallback: If no transition occurs, navigate after 500ms
        const fallbackTimer = setTimeout(() => {
          // Remove the event listener if fallback is triggered
          this.secondImage.removeEventListener('transitionend', handleTransitionEnd);
          navigate();
        }, 500);
      }

      preloadTargetImage() {
        // Get the main product image from the card
        const mainImage = this.querySelector('.product-card__image--main img');

        if (!mainImage) return;

        // Extract the base image URL and create high-res versions for preloading
        const imageSrc = mainImage.getAttribute('src') || mainImage.getAttribute('data-src');

        if (!imageSrc) return;

        // Create preload link for large image (for product detail page)
        const preloadLink = document.createElement('link');
        preloadLink.rel = 'preload';
        preloadLink.as = 'image';

        // Generate high-resolution image URL (1240px for desktop)
        // This matches the size used on product detail pages
        const highResUrl = this.getHighResImageUrl(imageSrc, 1240);
        preloadLink.href = highResUrl;

        // Add imagesrcset for responsive preloading
        preloadLink.setAttribute('imagesrcset',
          `${this.getHighResImageUrl(imageSrc, 620)} 620w, ` +
          `${this.getHighResImageUrl(imageSrc, 980)} 980w, ` +
          `${this.getHighResImageUrl(imageSrc, 1240)} 1240w, ` +
          `${this.getHighResImageUrl(imageSrc, 1620)} 1620w`
        );

        preloadLink.setAttribute('imagesizes', '(min-width: 1024px) 50vw, 100vw');
        preloadLink.fetchPriority = 'high';

        // Add to document head
        document.head.appendChild(preloadLink);
      }

      getHighResImageUrl(originalUrl, width) {
        // Shopify image URL pattern: replace width parameter
        // Example: image.jpg?v=123&width=450 -> image.jpg?v=123&width=1240
        if (originalUrl.includes('width=')) {
          return originalUrl.replace(/width=\d+/, `width=${width}`);
        } else if (originalUrl.includes('?')) {
          return `${originalUrl}&width=${width}`;
        } else {
          return `${originalUrl}?width=${width}`;
        }
      }
    }
  );
}
